import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Chip, IconButton, Tooltip } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ArticleIcon from "@mui/icons-material/Article";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import SentimentNeutralIcon from "@mui/icons-material/SentimentNeutral";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import { setModal } from "../../features/modal/ModalSlice";
import CustomButton from "../../components/Button/CustomButton";
import apiClient from "../../services/apiClient";

const CallAnalysis = () => {
  const dispatch = useDispatch();
  const modal = useSelector((state) => state.modal?.modal);

  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // API response data
  const [paginationInfo, setPaginationInfo] = useState({
    total_count: 0,
    total_pages: 0,
  });

  const [widgets, setWidgets] = useState({
    total_calls: 0,
    avg_duration: "0m 0s",
    avg_sentiment: 0,
    resolution_rate: 0,
    total_cost: 0,
  });

  useEffect(() => {
    fetchCalls();
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    if (modal?.isUpdatedIn === "CALL_ANALYSIS") {
      fetchCalls();
    }
  }, [modal]);

  const fetchCalls = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/call-analysis", {
        page: pagination.pageIndex + 1,
        page_size: pagination.pageSize,
      });

      if (response.status) {
        // Transform API data to match component structure
        const transformedCalls = response.table_data.map((call) => ({
          callId: call.call_id,
          date: call.created_at,
          assistant: call.agent,
          customer: call.customer,
          duration: call.duration_fmt,
          sentiment: call.sentiment.label,
          sentimentScore: call.sentiment.score / 100,
          topics: call.topics,
          outcome: call.outcome,
          cost: call.cost,
          hasRecording: true,
          hasTranscript: true,
        }));

        setCalls(transformedCalls);
        setPaginationInfo({
          total_count: response.pagination.total_count,
          total_pages: response.pagination.total_pages,
        });
        setWidgets(response.widgets);
      }
    } catch (error) {
      console.error("Error fetching calls:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (row) => {
    try {
      const response = await apiClient.get(`/call-analysis/${row.callId}`);
      if (response.status && response.data) {
        dispatch(setModal({ type: "VIEW_CALL_DETAILS", data: response.data }));
      }
    } catch (error) {
      console.error("Error fetching call details:", error);
      alert("Failed to fetch call details");
    }
  };

  const handlePlayRecording = (row) => {
    alert(`Playing recording for call ${row.callId}`);
  };

  const handleDownloadTranscript = async (row) => {
    try {
      const response = await apiClient.get(
        `/call-analysis/${row.callId}/download`,
        {},
        { responseType: "blob" }
      );

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `transcript_${row.callId}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading transcript:", error);
      alert("Failed to download transcript");
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case "Positive":
        return <SentimentSatisfiedAltIcon sx={{ color: "#4CAF50" }} />;
      case "Neutral":
        return <SentimentNeutralIcon sx={{ color: "#FF9800" }} />;
      case "Negative":
        return <SentimentDissatisfiedIcon sx={{ color: "#F44336" }} />;
      default:
        return null;
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case "Positive":
        return { bg: "#E8F5E9", color: "#2E7D32" };
      case "Neutral":
        return { bg: "#FFF3E0", color: "#E65100" };
      case "Negative":
        return { bg: "#FFEBEE", color: "#C62828" };
      default:
        return { bg: "#F5F5F5", color: "#666" };
    }
  };

  const getOutcomeColor = (outcome) => {
    const colors = {
      Resolved: "success",
      Completed: "success",
      "Follow-up Required": "warning",
      Transferred: "info",
      Escalated: "error",
    };
    return colors[outcome] || "default";
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "callId",
        header: "Call ID",
        size: 140,
        Cell: ({ row }) => (
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "monospace" }}>
              {row.original.callId}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.date}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "customer",
        header: "Customer",
        size: 150,
        Cell: ({ cell, row }) => (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {cell.getValue()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              via {row.original.assistant}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "duration",
        header: "Duration",
        size: 100,
        Cell: ({ row }) => (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {row.original.duration}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "sentiment",
        header: "Sentiment",
        size: 130,
        Cell: ({ row }) => {
          const colors = getSentimentColor(row.original.sentiment);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {getSentimentIcon(row.original.sentiment)}
              <Chip
                label={row.original.sentiment}
                size="small"
                sx={{
                  backgroundColor: colors.bg,
                  color: colors.color,
                  fontWeight: 600,
                }}
              />
            </Box>
          );
        },
      },
      {
        accessorKey: "topics",
        header: "Topics",
        size: 200,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {row.original.topics.slice(0, 2).map((topic, idx) => (
              <Chip
                key={idx}
                label={topic}
                size="small"
                sx={{
                  fontSize: 10,
                  height: 20,
                  backgroundColor: "#E3F2FD",
                  color: "#1565C0",
                }}
              />
            ))}
            {row.original.topics.length > 2 && (
              <Chip
                label={`+${row.original.topics.length - 2}`}
                size="small"
                sx={{
                  fontSize: 10,
                  height: 20,
                  backgroundColor: "#F5F5F5",
                  color: "#666",
                }}
              />
            )}
          </Box>
        ),
      },
      {
        accessorKey: "outcome",
        header: "Outcome",
        size: 140,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue()}
            size="small"
            color={getOutcomeColor(cell.getValue())}
            sx={{ fontWeight: 500 }}
          />
        ),
      },
      {
        accessorKey: "cost",
        header: "Cost",
        size: 80,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={600} color="primary">
            ${cell.getValue().toFixed(2)}
          </Typography>
        ),
      },
    ],
    []
  );

  return (


    <>

    {/* Header Section - White Background */}
    <div className="pageTitle1 d-flex justify-content-between align-items-center px-5 py-4">
      <div>
        <h3>Tools</h3>
        <p>Manage pre-built tools and create custom API integrations</p>
      </div>

      <CustomButton
        icon={<DownloadIcon />}
        iconPosition="left"
        text="Export Report"
        variant="outlined"
      />

    </div>
    
    <Box>

      {/* Stats Cards Section - Separate */}
      <Box sx={{ px: 4, pt: 3, pb: 2 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#111827" sx={{ mb: 0.5 }}>
              {widgets.total_calls}
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Total Calls Analyzed
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#3B82F6" sx={{ mb: 0.5 }}>
              {widgets.avg_duration}
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Average Duration
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#10B981" sx={{ mb: 0.5 }}>
              {widgets.avg_sentiment}%
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Average Sentiment
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#F59E0B" sx={{ mb: 0.5 }}>
              {widgets.resolution_rate}%
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Resolution Rate
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#EF4444" sx={{ mb: 0.5 }}>
              ${widgets.total_cost.toFixed(2)}
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Total Cost
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Table Section */}
      <Box sx={{ px: 4, pb: 3 }}>
        <Box sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: 2,
          border: "1px solid #E5E7EB",
          overflow: "hidden",
          p: 2
        }}>
          <MaterialReactTable
            columns={columns}
            data={calls}
            manualPagination
            rowCount={paginationInfo.total_count}
            onPaginationChange={setPagination}
            state={{
              isLoading: loading,
              pagination: pagination,
            }}
            enableRowActions
            positionActionsColumn="last"
            renderRowActions={({ row }) => (
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {row.original.hasRecording && (
                  <Tooltip title="Play Recording">
                    <IconButton
                      size="small"
                      onClick={() => handlePlayRecording(row.original)}
                      sx={{ color: "success.main" }}
                    >
                      <PlayArrowIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="View Details">
                  <IconButton
                    size="small"
                    onClick={() => handleViewDetails(row.original)}
                    sx={{ color: "primary.main" }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
                {row.original.hasTranscript && (
                  <Tooltip title="Download Transcript">
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadTranscript(row.original)}
                      sx={{ color: "info.main" }}
                    >
                      <ArticleIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
            enableColumnFilters
            enableGlobalFilter
            enableSorting
            enablePagination
            muiTablePaperProps={{
              elevation: 0,
              sx: {
                borderRadius: 0,
                border: "none",
                boxShadow: "none"
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                backgroundColor: "#F9FAFB",
                fontWeight: 600,
              },
            }}
            muiTableBodyProps={{
              sx: {
                '& tr:hover': {
                  backgroundColor: '#F9FAFB',
                },
              },
            }}
          />
        </Box>
      </Box>
    </Box>
    
    
    </>



  );
};

export default CallAnalysis;
